import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import journeyProp from './propTypes/journeyProp';
import {
  calculatePromoPercentage,
  promotionEndingMessage,
} from '../../services/utilityFunctions';
import i18n from '../../services/i18n';
import PriceDisplay from '../../commonComponents/PriceDisplay';
import AcademyFooterContainer from '../../commonComponents/container/AcademyFooterContainer';
import LanguagesIcon from '../../commonComponents/icons/LanguagesIcon';
import CertificateIcon from '../../commonComponents/icons/CertificateIcon';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';

export default function JourneyItem({
  t,
  journey,
  languages,
  sessionId,
  showPromotion,
  onEnroll,
  enrollLabel,
  isListPage,
  showTeaserVideo,
}) {
  const {
    image,
    reward,
    language,
    summary,
    title,
    userPrice,
    userPromotionPrice,
    steps,
    certificate,
    slug,
    promotionPriceDateEnd,
  } = journey;
  const { loggedUser } = useContext(LoggedUserContext);

  // Journey Image
  const imageURL =
    image && image.thumb && image.thumb !== ''
      ? image.thumb
      : '/images/no_image_upload.png';

  const skillLevels = useMemo(
    () =>
      journey.skillLevels && journey.skillLevels.length > 0
        ? journey.skillLevels.map(hs => hs.name).join(', ')
        : '',
    [journey?.skillLevels],
  );

  const journeyRewards = useMemo(() => {
    if (!reward) return '';

    const {
      coupon,
      voucher,
      userLevel,
      userType,
      chatGroup,
      coins,
      competenciesDomains,
    } = reward;

    const parts = [];

    if (coins > 0) parts.push(t('rewards_coins'));
    if (coupon) parts.push(t('rewards_coupons'));
    if (voucher) parts.push(t('rewards_vouchers'));
    if (userLevel > 0) parts.push(t('reward.userLevel'));
    if (userType?.length > 0) parts.push(t('reward.userType'));
    if (chatGroup) parts.push(t('reward.chatGroup'));
    if (competenciesDomains?.length > 0) {
      parts.push(t('reward.competenciesDomains'));
    }

    return parts.length > 0 ? `${parts.join(', ')}.` : '';
  }, [reward, t]);

  // Footer - Language
  const languageValue = useMemo(() => {
    let languageStr = null;
    if (languages && languages.length > 0) {
      const lang = languages.find(l => l.code === language) || {};
      languageStr = lang.native || t('all');
    }
    return languageStr;
  }, [language, languages]);

  const containerClass = cx(
    'm-portlet',
    'm-portlet--rounded',
    'm-portlet--rounded-force',
    'academy-session-item',
  );
  const url = sessionId
    ? `/journey/${slug}?sessionContext=${sessionId}`
    : `/journey/${slug}`;

  const promotionMessage = promotionEndingMessage(promotionPriceDateEnd);
  const promoPercentage = calculatePromoPercentage(
    userPrice?.total,
    userPromotionPrice?.total,
  );
  const promoPercentageMessage =
    promoPercentage &&
    i18n.__('product.promotion.percentageOff', promoPercentage);
  return (
    <div>
      {showPromotion && promotionMessage?.length > 0 && (
        <div className="flag-academy flag-academy-promotion">{`${promoPercentageMessage} ${promotionMessage}`}</div>
      )}
      <div className={containerClass}>
        <div className="m-portlet__body session-container-body session-container-content">
          <div className="row">
            {isListPage && (
              <div className="col-lg-2 col-12">
                <div
                  className={cx('session-image-wrapper', {
                    'teaser-card': showTeaserVideo && journey?.teaserVideo?.url,
                  })}
                >
                  <img className="session-image" src={imageURL} alt={title} />
                  {showTeaserVideo && journey?.teaserVideo?.url && (
                    <span
                      className="teaser-play-btn"
                      role="button"
                      aria-label={t('watchTrailer')}
                      data-trailer-url={journey.teaserVideo.url}
                    >
                      <i className="fa fa-play" />
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className={`${isListPage ? 'col-lg-10' : 'col-lg-12'} col-12`}>
              <div className="row">
                <div className="col-lg-9 col-12">
                  <div className="session-details">
                    <div className="session-header">
                      <span className="session-header-name">{title}</span>
                    </div>
                    <div className="session-description-and-progress journey-fix-description-margins">
                      <div className="session-description">{summary}</div>
                    </div>
                  </div>
                </div>
                <div
                  className="col-lg-3 col-12 px-0"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <PriceDisplay
                    type="journey"
                    t={t}
                    userPrice={userPrice}
                    userPromotionPrice={userPromotionPrice}
                    withWrapper
                  />
                </div>
                {!isListPage && skillLevels.length > 0 && (
                  <div className="col-lg-12 col-12">
                    <div className="skill-container-course">
                      <div className="label">{`${t(
                        'academy.skill.levels',
                      )}:`}</div>
                      <div className="skills">{skillLevels}</div>
                    </div>
                  </div>
                )}
                {isListPage && journeyRewards?.trim()?.length > 0 && (
                  <div className="col-lg-12 col-12">
                    <div className="skill-container-course">
                      <div className="label">{`${t(
                        'academy.rewards.journey',
                      )}:`}</div>
                      <div className="skill-icon-container">
                        <img
                          className="skill-icon"
                          src="/images/trophies/trophy.svg"
                          alt="trophy"
                        />
                      </div>
                      <div className="skills">{journeyRewards}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="m-portlet__foot session-container-footer row">
          <div
            className="footer-info-container"
            style={!isListPage ? { marginLeft: '1.9rem' } : {}}
          >
            {steps?.length > 0 && (
              <AcademyFooterContainer
                faIcon="la la-list"
                text={t('journey.number.of.steps', steps.length || 0)}
              />
            )}
            {languageValue && languageValue?.length > 0 && (
              <AcademyFooterContainer
                customIcon={<LanguagesIcon />}
                text={languageValue || ''}
              />
            )}
            {certificate && certificate.enabled && (
              <AcademyFooterContainer
                customIcon={<CertificateIcon />}
                text={t('reward.certificate')}
              />
            )}
          </div>

          <div className="col-lg-2 col-12 offset-lg-1 session-itemList-btns">
            {isListPage && !onEnroll && (
              <div className="session-actions row">
                <a
                  href={url}
                  className="btn btn-sm m-btn--pill btn-dynamic navigation-button col-12"
                >
                  {t('academy.open.journey')}
                </a>
              </div>
            )}
            {!isListPage && onEnroll && (
              <button
                type="button"
                onClick={onEnroll}
                className="btn btn-sm m-btn--pill btn-dynamic navigation-button col-12"
              >
                {loggedUser
                  ? enrollLabel || t('sessions_enroll')
                  : t('academy_enroll')}
              </button>
            )}
            {/* BRAVON-6462: a journey whose payment is outstanding shows
                nothing here — the caller withholds `onEnroll` and the carousel
                overlay carries the status on its own. */}
          </div>
        </div>
      </div>
    </div>
  );
}

JourneyItem.propTypes = {
  t: PropTypes.func.isRequired,
  journey: PropTypes.shape(journeyProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  sessionId: PropTypes.string,
  showPromotion: PropTypes.bool,
  onEnroll: PropTypes.func,
  enrollLabel: PropTypes.string,
  isListPage: PropTypes.bool,
  showTeaserVideo: PropTypes.bool,
};

JourneyItem.defaultProps = {
  sessionId: null,
  showPromotion: false,
  onEnroll: null,
  enrollLabel: null,
  isListPage: true,
  showTeaserVideo: false,
};
